import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { popularpSEOTemplates } from '../../utility/pSEO/urlGenerator';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * ExplorePSEOPage
 * Displays popular pSEO templates as cards organized by listing intent
 * Serves as a discovery hub for users to browse major search combinations
 * SEO-optimized with React Helmet, structured data, and semantic HTML
 */

interface SeoTemplate {
  url: string;
  title: string;
  volume?: number;
}

// Group templates by intent (rent, sale, share)
const groupTemplatesByIntent = (templates: SeoTemplate[]): Record<string, SeoTemplate[]> => {
  return {
    rent: templates.filter((t) => t.url.startsWith('/rent')),
    sale: templates.filter((t) => t.url.startsWith('/sale')),
    share: templates.filter((t) => t.url.startsWith('/share')),
  };
};

const ExplorePSEOPage: React.FC = () => {
  const groupedTemplates = groupTemplatesByIntent(popularpSEOTemplates);

  const intentConfig = {
    rent: {
      label: 'For Rent',
      description: 'Discover rental properties across Ireland',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
      badgeColor: 'bg-blue-100 text-blue-800',
    },
    sale: {
      label: 'For Sale',
      description: 'Find homes for sale in your ideal location',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      buttonColor: 'bg-green-600 hover:bg-green-700',
      badgeColor: 'bg-green-100 text-green-800',
    },
    share: {
      label: 'Room Shares',
      description: 'Explore room share and house share options',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      buttonColor: 'bg-purple-600 hover:bg-purple-700',
      badgeColor: 'bg-purple-100 text-purple-800',
    },
  };

  const canonicalUrl = 'https://rentsmart.ie/explore-properties';

  return (
    <>
      <Helmet>
        <title>Explore Popular Property Searches | Rent, Sale, Share | RentSmart.ie</title>
        <meta
          name="description"
          content="Explore popular property searches across Ireland. Browse rentals, homes for sale, and room shares in Dublin, Cork, Galway, and more major locations."
        />
        <meta
          name="keywords"
          content="property search Ireland, rental properties, homes for sale, room shares, Dublin rentals, Cork properties, Galway apartments, property listings"
        />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Explore Popular Property Searches | RentSmart.ie" />
        <meta
          property="og:description"
          content="Discover rental properties, homes for sale, and room shares across Ireland's major cities."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />

        {/* Structured Data (Schema.org) */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Explore Popular Property Searches',
            description:
              'Discover popular property searches across Ireland organized by rental, sale, and share options',
            url: canonicalUrl,
            mainEntity: {
              '@type': 'ItemCollection',
              numberOfItems: popularpSEOTemplates.length,
              itemListElement: popularpSEOTemplates.slice(0, 20).map((template, index) => ({
                '@type': 'Thing',
                position: index + 1,
                name: template.title,
                url: `https://rentsmart.ie${template.url}`,
              })),
            },
          })}
        </script>
      </Helmet>

      <div className="bg-gradient-to-b from-slate-50 to-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Explore Popular Property Searches
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover the most popular property searches across Ireland. Browse curated collections
              of rental properties, homes for sale, and room shares in your favorite locations.
            </p>
          </div>

          {/* Intent-Based Sections */}
          <div className="space-y-16">
            {Object.entries(groupedTemplates).map(
              ([intent, templates]) =>
                templates.length > 0 && (
                  <section key={intent} className="space-y-6">
                    {/* Section Header */}
                    <div
                      className={`${intentConfig[intent as keyof typeof intentConfig].bgColor} rounded-lg p-6 border-2 ${intentConfig[intent as keyof typeof intentConfig].borderColor}`}
                    >
                      <h2 className="text-2xl font-bold text-gray-900">
                        {intentConfig[intent as keyof typeof intentConfig].label}
                      </h2>
                      <p className="text-gray-600 mt-2">
                        {intentConfig[intent as keyof typeof intentConfig].description}
                      </p>
                    </div>

                    {/* Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {templates.map((template, index) => (
                        <Link key={`${intent}-${index}`} to={template.url} className="group h-full">
                          <div className="h-full bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-gray-200 hover:border-blue-400 flex flex-col">
                            {/* Card Header */}
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors mb-2 line-clamp-3">
                                {template.title}
                              </h3>

                              {/* Search Volume Badge */}
                              {template.volume && (
                                <div className="mt-4 mb-4">
                                  <span
                                    className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${intentConfig[intent as keyof typeof intentConfig].badgeColor}`}
                                  >
                                    ~{(template.volume / 1000).toFixed(1)}k searches/month
                                  </span>
                                </div>
                              )}

                              {/* URL Display */}
                              <p className="text-sm text-gray-500 font-mono break-all">
                                {template.url}
                              </p>
                            </div>

                            {/* Card Footer with Arrow */}
                            <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                              <span className="text-sm font-medium text-blue-600">Browse →</span>
                              <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )
            )}
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Don't see what you're looking for?</h2>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Use our advanced search to find the perfect property with custom filters for price,
              location, amenities, and more.
            </p>
            <Link
              to="/browse"
              className="inline-block bg-white text-blue-600 font-semibold px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors"
            >
              Advanced Search
            </Link>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>

            <details className="bg-white rounded-lg p-6 border border-gray-200">
              <summary className="cursor-pointer font-semibold text-gray-900 hover:text-blue-600">
                How are these popular searches selected?
              </summary>
              <p className="mt-4 text-gray-600">
                These searches are based on aggregate search volume data and user trends across
                RentSmart.ie. They represent the most popular property combinations being searched
                by users in Ireland.
              </p>
            </details>

            <details className="bg-white rounded-lg p-6 border border-gray-200">
              <summary className="cursor-pointer font-semibold text-gray-900 hover:text-blue-600">
                Can I combine multiple filters?
              </summary>
              <p className="mt-4 text-gray-600">
                Yes! Each card shows a popular combination, but you can mix and match any filters.
                Click any search and then use the advanced filters on the search page to further
                refine your results.
              </p>
            </details>

            <details className="bg-white rounded-lg p-6 border border-gray-200">
              <summary className="cursor-pointer font-semibold text-gray-900 hover:text-blue-600">
                Are these results updated in real-time?
              </summary>
              <p className="mt-4 text-gray-600">
                The search results are updated regularly as new properties are added to our
                platform. Each search shows the most current available listings matching your
                criteria.
              </p>
            </details>
          </div>
        </div>
      </div>

      {/* SEO-Rich Meta for Accessibility */}
      <div className="sr-only">
        <h1>Explore Popular Property Searches on RentSmart.ie</h1>
        <p>
          Discover the most searched property combinations across Ireland, including rentals, homes
          for sale, and room shares in Dublin, Cork, Galway, and other major cities.
        </p>
      </div>
    </>
  );
};

export default ExplorePSEOPage;
